import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { BaseBlockView } from '../base-block.view';
import { inject, observer } from 'mobx-react';
import { IInjectedProps } from '../../../interface/component/injected-props.interface';
import { TextImageModel } from '../../../model/block';
import { Carousel } from '../../carousel/carousel';
import { action, computed, observable } from 'mobx';
import styled from 'styled-components';
import { DraftToHtml } from '../../draft-to-html/draft-to-html';
import { getImageURL } from '../../../util/url/get-image-url.util';
import { IMAGE_HEIGHT } from '../../../constants';
import { centerOfScreenSizes, getCenterOfPageStyle } from '../../../util/style/get-center-of-page-style';
import { tabletMedia } from '../../../util/style/tablet-media';
import { CmsImage } from '../../cms-image/cms-image';
import { largeMobileMedia } from '../../../util/style/large-mobile-media';

interface ITextImageBlockWrapper {
  blockStyle: 'wide' | 'normal';
}

const TextImageBlockWrapper = styled.div<ITextImageBlockWrapper & React.HTMLAttributes<HTMLDivElement>>`
  width: 100%;
  position: relative;
  display: flex;
  min-height: ${IMAGE_HEIGHT}px;
  overflow: hidden;
  align-items: stretch;
  ${tabletMedia(`
    flex-direction: column;
    height: auto;
  `)}
  ${(props: ITextImageBlockWrapper) => props.blockStyle === 'wide' ? '' : getCenterOfPageStyle()(props)}
`;

const ImageContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  justify-content: center;
  align-items: center;
  display: flex;
  overflow: hidden;
  height: 100%;
  min-height: ${IMAGE_HEIGHT}px;
  width: 100%;
  margin: 0px;
  padding: 0px;
  flex: 0 0 50%;
  ${tabletMedia(`
    position: relative;
    height: ${IMAGE_HEIGHT}px;
    flex: 1 0 auto;
  `)}
`;

const CauroselContainer = styled.div`
  position: relative;
  flex: 0 0 50%;
  width: 50%;
  ${tabletMedia(`
    width: 100%;
    flex: 0 0 ${IMAGE_HEIGHT};
    height: ${IMAGE_HEIGHT};
  `)}
`;

const textOnTablet = tabletMedia(`
    max-height: none;
    flex: 1 0 auto;
    max-width: none;
`);

const textOnMobile = largeMobileMedia(`
    padding: 20px 13px;
    margin: 0;
    height: auto;
    margin: 0;
    margin-left: ${centerOfScreenSizes.find((size) => size.label === 'LargeMobile').marginLeft * 100}%;
    width: ${centerOfScreenSizes.find((size) => size.label === 'LargeMobile').width * 100}%;
`);

const TextWrapper = styled.div`
  display: flex;
  overflow: hidden;
  flex: 0 0 50%;
  justify-content: center;
  word-wrap: break-word;
`;

const NormalTextWrapper = styled(TextWrapper)`
  margin: 40px;
  max-width: calc(50% - 80px);
  height: calc(100% - 80px);
  ${textOnTablet}
  ${textOnMobile}
`;

const WideTextWrapper = styled(TextWrapper)`
  margin: 70px;
  max-width: calc(50% - 140px);
  height: calc(100% - 140px);
  ${textOnTablet}
  ${textOnMobile}
`;

@inject('state')
@observer
export class TextImageView extends BaseBlockView<TextImageModel, IInjectedProps> {
  carouselRef;
  @observable width: number = 0;

  @computed
  get images() {
    const content = this.props.content;
    return content.images.map((currentImage, index) => {
      if (currentImage) {
        const imageURL = getImageURL(this.props.state, currentImage, this.width + 100, this.props.dataItem);
        if (imageURL) {
          return <CmsImage
            src={currentImage}
            key={index}
            imagePercentage={.5}
            blockMentionData={this.props.dataItem}
            sizeStyle={this.props.content.imagesSizeStyle}
          />;
        }
      }
    }).filter(img => Boolean(img));
  }

  @action
  componentDidMount() {
    if (this.carouselRef) {
      this.width = ReactDOM.findDOMNode(this.carouselRef).getBoundingClientRect().width;
    }
  }

  @action
  componentDidUpdate() {
    if (this.carouselRef) {
      const newWidth = ReactDOM.findDOMNode(this.carouselRef).getBoundingClientRect().width;
      if (newWidth !== this.width) {
        this.width = newWidth;
      }
    } else {
      this.width = 0;
    }
  }

  render() {
    const isWide = this.props.content.style === 'wide';
    const text = isWide ?
      <WideTextWrapper><DraftToHtml
        draft={this.props.content.text}
        dataItem={this.props.dataItem}
        inEditor={this.props.inEditor}
      /></WideTextWrapper> :
      <NormalTextWrapper><DraftToHtml
        draft={this.props.content.text}
        dataItem={this.props.dataItem}
        inEditor={this.props.inEditor}
      /></NormalTextWrapper>;
    const carousel = <CauroselContainer ref={(c) => this.carouselRef = c}>
      <Carousel
        autoLoop={true}
        images={this.images}
        wrapper={ImageContainer as any}
        transitionDuration={this.props.content.transitionDuration}
        transitionTime={this.props.content.transitionTime}
      />
    </CauroselContainer>;
    return (
      <TextImageBlockWrapper blockStyle={this.props.content.style}>
        {this.props.content.textBlockAlignment === 'left' ? text : carousel}
        {this.props.content.textBlockAlignment === 'left' ? carousel : text}
      </TextImageBlockWrapper>
    );
  }
}
